/**
 * External Handling — write paths (VALE-841).
 *
 * Every endpoint here is gated server-side by the `external-handling` feature flag
 * (D6): a firm without the grant gets a 403, which is what keeps the whole feature
 * disabled until it is deliberately switched on per firm.
 */

'use client';

import { directApiClient } from '@/lib/api/direct-client';
import type { MatterInstitutionActionResult } from '../matter-institutions/types';
import type { Handling, SetHandlingRequest } from './types';

/** Set or revert handling on one institution. */
export async function setInstitutionHandlingAction(
  matterId: number,
  institutionId: number,
  data: SetHandlingRequest
): Promise<MatterInstitutionActionResult<Handling>> {
  try {
    const result = await directApiClient.put<Handling>(
      `/matters/${matterId}/institutions/${institutionId}`,
      data
    );
    return { success: true, data: result };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to update handling'
    };
  }
}

/**
 * Set or revert handling on many institutions at once (EH-002, EH-005).
 *
 * Deliberately symmetrical: the same endpoint serves both directions, because a
 * one-click hand-over with a fifteen-click undo is an asymmetry users pay for.
 *
 * NOTE the route is `/institutions/handling`, not `/institutions/:id` — it is
 * declared before the `:institutionId` route on the backend precisely so that
 * "handling" is not parsed as an id.
 */
export async function bulkSetInstitutionHandlingAction(
  matterId: number,
  institutionIds: number[],
  data: SetHandlingRequest
): Promise<MatterInstitutionActionResult<{ updated: number }>> {
  try {
    const result = await directApiClient.put<{ updated: number }>(
      `/matters/${matterId}/institutions/handling`,
      { institutionIds, ...data }
    );
    return { success: true, data: result };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to update handling'
    };
  }
}

/**
 * Set or revert handling on an inventory item that has no institution — a house,
 * a car (EH-CR-007). An institution-backed item inherits from its institution and
 * offers no item-level control at all.
 */
export async function setInventoryItemHandlingAction(
  matterId: number,
  inventoryItemId: number,
  data: SetHandlingRequest
): Promise<MatterInstitutionActionResult<Handling>> {
  try {
    const result = await directApiClient.put<Handling>(
      `/matters/${matterId}/inventory-items/${inventoryItemId}/handling`,
      data
    );
    return { success: true, data: result };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to update handling'
    };
  }
}
